import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { StarRating } from '../components/StarRating';
import { validatePassword } from '../lib/passwordRules';
import { listLandlordRequests, STATUS_LABEL } from '../lib/creditProvider';
import { isEligibleForBadge, BADGE_PRICE_USD } from '../lib/verifiedBadge';

export default function ManagerDashboard({ user }) {
  const navigate = useNavigate();

  const [managerProfile, setManagerProfile] = useState(null);
  const [reviewsAboutMe, setReviewsAboutMe] = useState([]);
  const [myReviews, setMyReviews] = useState([]);
  const [viewedProfiles, setViewedProfiles] = useState([]);
  const [creditRequests, setCreditRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showChangePw, setShowChangePw] = useState(false);
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showNewPw, setShowNewPw] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);
  const [pwSaving, setPwSaving] = useState(false);
  const [showBadgeInfo, setShowBadgeInfo] = useState(false);
  const [displayName, setDisplayName] = useState(user?.name || '');
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(user?.name || '');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameMsg, setNameMsg] = useState(null);

  const handleSaveName = async () => {
    const trimmed = nameInput.trim();
    if (!trimmed) { setNameMsg({ type: 'error', text: 'Name cannot be empty.' }); return; }
    setNameSaving(true); setNameMsg(null);
    const { error } = await supabase.auth.updateUser({ data: { name: trimmed } });
    if (error) { setNameMsg({ type: 'error', text: error.message }); setNameSaving(false); return; }
    // Keep the profiles table in sync (used by the admin user list).
    if (user?.id) { await supabase.from('profiles').update({ name: trimmed }).eq('id', user.id); }
    setDisplayName(trimmed);
    setEditingName(false);
    setNameSaving(false);
    setNameMsg({ type: 'success', text: 'Name updated.' });
    setTimeout(() => setNameMsg(null), 1800);
  };

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    async function loadData() {
      const { data: mProf } = await supabase.from('managers').select('*').ilike('name', user.name).maybeSingle();
      setManagerProfile(mProf || null);

      let aboutMe = [];
      if (mProf?.id) {
        const { data } = await supabase.from('reviews').select('*').eq('profile_id', mProf.id).eq('profile_type', 'manager').order('created_at', { ascending: false });
        aboutMe = data || [];
      }
      setReviewsAboutMe(aboutMe);

      const { data: mine } = await supabase.from('reviews').select('*').eq('author', user.name).order('created_at', { ascending: false });
      const enriched = await Promise.all((mine || []).map(async (r) => {
        const table = r.profile_type === 'manager' ? 'managers' : 'tenants';
        const { data: prof } = await supabase.from(table).select('id, name').eq('id', r.profile_id).maybeSingle();
        return { ...r, profileName: prof?.name || 'Unknown profile' };
      }));
      setMyReviews(enriched);

      try {
        const stored = JSON.parse(localStorage.getItem('rentrep_recently_viewed') || '[]');
        setViewedProfiles(stored.slice(0, 6));
      } catch(e) {}

      try {
        const reqs = await listLandlordRequests({ landlordId: user.id });
        setCreditRequests(reqs);
      } catch(e) {}

      setLoading(false);
    }
    loadData();
  }, [user, navigate]);

  const cardStyle = {
    background: '#FAF6EC',
    borderRadius: 16,
    borderTop: '1px solid rgba(184,137,58,0.22)',
    borderRight: '1px solid rgba(184,137,58,0.22)',
    borderBottom: '1px solid rgba(184,137,58,0.22)',
    borderLeft: '1px solid rgba(184,137,58,0.22)',
    boxShadow: '0 2px 8px rgba(120,90,30,0.06)',
  };

  const isManager = user?.role === 'manager';
  const plan = isManager ? 'Gold' : 'No active plan';
  const planPrice = isManager ? '$24.99/mo · unlimited unlocks' : null;

  const reputation = reviewsAboutMe.length === 0 ? null
    : reviewsAboutMe.reduce((s, r) => s + (r.rating || 0), 0) / reviewsAboutMe.length;

  const pwChecks = validatePassword(newPw);
  const pwOk = pwChecks.every(c => c.ok);
  const pwMatch = newPw.length > 0 && newPw === confirmPw;

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwMsg(null);
    if (!pwOk) { setPwMsg({ type: 'error', text: 'Password does not meet all the requirements below.' }); return; }
    if (!pwMatch) { setPwMsg({ type: 'error', text: 'The two passwords do not match.' }); return; }
    setPwSaving(true);
    const { error } = await supabase.auth.updateUser({ password: newPw });
    setPwSaving(false);
    if (error) { setPwMsg({ type: 'error', text: error.message }); return; }
    setPwMsg({ type: 'success', text: 'Password updated successfully.' });
    setNewPw(''); setConfirmPw('');
    setTimeout(() => { setShowChangePw(false); setPwMsg(null); }, 1800);
  };

  const handleDeleteMyReview = async (reviewId) => {
    if (!window.confirm('Delete this review? This cannot be undone.')) return;
    const { error } = await supabase.from('reviews').delete().eq('id', reviewId);
    if (error) { alert('Could not delete: ' + error.message); return; }
    setMyReviews(prev => prev.filter(r => r.id !== reviewId));
  };

  if (!user) return null;

  return (
    <div className="page-wrap" style={{ background: '#F5EFE3' }}>
      <div className="container" style={{ paddingTop: 20, paddingBottom: 32 }}>

        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 11, color: '#B8893A', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}>Your account</div>
          <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 26, fontWeight: 400, color: '#0F2C4E', lineHeight: 1.2 }}>Welcome back, {(displayName || user.name)?.split(' ')[0]}.</div>
        </div>

        {/* ─── Account ─── */}
        <SectionHeader title="Account" subtitle="Your company info, login, and password" />
        <div style={{ ...cardStyle, padding: 18, marginBottom: 24, borderRadius: '0 0 16px 16px', borderTop: 'none' }}>
          {/* Editable name */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(184,137,58,0.12)', fontSize: 13, gap: 10, flexWrap: 'wrap' }}>
            <span style={{ color: '#6B6358' }}>Name</span>
            {editingName ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end', flex: 1 }}>
                <input
                  value={nameInput}
                  onChange={e => setNameInput(e.target.value)}
                  autoFocus
                  style={{ padding: '6px 10px', borderRadius: 8, border: '1.5px solid rgba(184,137,58,0.45)', background: '#fff', fontSize: 13, minWidth: 160 }}
                />
                <button onClick={handleSaveName} disabled={nameSaving} style={{ background: '#0F2C4E', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 50, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  {nameSaving ? 'Saving…' : 'Save'}
                </button>
                <button onClick={() => { setEditingName(false); setNameInput(displayName); setNameMsg(null); }} style={{ background: 'transparent', color: '#6B6358', border: '1.5px solid rgba(184,137,58,0.45)', padding: '6px 12px', borderRadius: 50, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ color: '#0F2C4E', fontWeight: 500 }}>{displayName || '—'}</span>
                <button onClick={() => { setEditingName(true); setNameInput(displayName); }} style={{ background: 'none', border: 'none', color: '#1A4C8B', fontSize: 12, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>
                  Edit
                </button>
              </div>
            )}
          </div>
          {nameMsg && (
            <div style={{ fontSize: 12, padding: '4px 0', color: nameMsg.type === 'success' ? '#1A7F37' : '#C0392B' }}>
              {nameMsg.type === 'success' ? '✓ ' : '⚠️ '}{nameMsg.text}
            </div>
          )}
          <Row label="Email" value={user.email} />
          <Row label="Role" value="Property Management" />
          {managerProfile?.name && <Row label="Company / profile" value={managerProfile.name} />}
          {managerProfile?.address && <Row label="Address" value={managerProfile.address} />}
          {managerProfile?.phone && <Row label="Phone" value={managerProfile.phone} />}
          {managerProfile?.neighborhood && <Row label="City" value={managerProfile.neighborhood} />}
          <div style={{ borderTop: '1px solid rgba(184,137,58,0.22)', marginTop: 12, paddingTop: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0F2C4E' }}>Password</div>
                <div style={{ fontSize: 12, color: '#6B6358', marginTop: 2 }}>Last changed: managed by Supabase Auth</div>
              </div>
              <button onClick={() => { setShowChangePw(s => !s); setPwMsg(null); }} style={btnOutline}>
                {showChangePw ? 'Cancel' : 'Change password'}
              </button>
            </div>
            {showChangePw && (
              <form onSubmit={handleChangePassword} style={{ marginTop: 12 }}>
                <div className="form-group">
                  <label>New password</label>
                  <div style={{ position: 'relative' }}>
                    <input type={showNewPw ? 'text' : 'password'} value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="Create a strong password" required minLength={8} style={{ paddingRight: 40, width: '100%', boxSizing: 'border-box' }} />
                    <button type="button" onClick={() => setShowNewPw(s => !s)} style={eyeBtn} aria-label={showNewPw ? 'Hide password' : 'Show password'}>
                      {showNewPw ? '🙈' : '👁'}
                    </button>
                  </div>
                  {newPw.length > 0 && (
                    <div style={{ marginTop: 8, padding: '10px 12px', background: 'rgba(184,137,58,0.10)', borderRadius: 8, border: '1px solid rgba(184,137,58,0.22)' }}>
                      {pwChecks.map(c => (
                        <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: c.ok ? '#1A7F37' : '#6B6358', lineHeight: 1.6 }}>
                          <span style={{ width: 14, display: 'inline-flex', justifyContent: 'center', fontWeight: 700 }}>{c.ok ? '✓' : '○'}</span>
                          {c.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>Confirm new password</label>
                  <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} placeholder="Re-enter the same password" required style={{
                    width: '100%', boxSizing: 'border-box',
                    borderColor: confirmPw && !pwMatch ? '#C0392B' : (pwMatch ? '#1A7F37' : undefined),
                  }} />
                  {confirmPw && !pwMatch && <div style={{ marginTop: 6, fontSize: 12, color: '#C0392B' }}>⚠️ The passwords don't match yet.</div>}
                  {pwMatch && <div style={{ marginTop: 6, fontSize: 12, color: '#1A7F37' }}>✓ Passwords match.</div>}
                </div>
                {pwMsg && (
                  <div style={{ fontSize: 13, marginBottom: 10, color: pwMsg.type === 'success' ? '#1A7F37' : '#C0392B' }}>
                    {pwMsg.type === 'success' ? '✓ ' : '⚠️ '}{pwMsg.text}
                  </div>
                )}
                <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%', opacity: pwSaving ? 0.7 : 1 }} disabled={pwSaving}>
                  {pwSaving ? 'Updating...' : 'Update password'}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* ─── Plan & Payment ─── */}
        <SectionHeader title="Plan & Payment" subtitle="What you're on, how to upgrade, payment method" />
        <div style={{ ...cardStyle, padding: 18, marginBottom: 24, borderRadius: '0 0 16px 16px', borderTop: 'none' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 11, color: '#B8893A', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>Current plan</div>
              <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 22, fontWeight: 400, color: '#0F2C4E' }}>{plan}</div>
              {planPrice && <div style={{ fontSize: 13, color: '#6B6358', marginTop: 2 }}>{planPrice}</div>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 8, minWidth: 160 }}>
              {isManager && <span style={pillBtnGoldFill}>ACTIVE</span>}
              <button
                onClick={() => {
                  if (window.confirm('Cancel your plan? You will lose access to paid features at the end of the current billing period.')) {
                    alert('Plan cancellation will be wired to Stripe in Phase 2. For now, email support@renterrep.com.');
                  }
                }}
                style={pillBtnCream}
              >
                Delete plan
              </button>
            </div>
          </div>
          <div style={{ borderTop: '1px solid rgba(184,137,58,0.22)', marginTop: 14, paddingTop: 14, display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 11, color: '#B8893A', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>Payment method</div>
              <div style={{ fontSize: 12, color: '#6B6358', lineHeight: 1.6 }}>
                Free during launch — no payment method required yet. Once paid tiers turn on, you'll add a card here.
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 8, minWidth: 160 }}>
              <button
                onClick={() => alert('Payment method management will be wired to Stripe in Phase 2.')}
                style={pillBtnCream}
              >
                Manage payment
              </button>
            </div>
          </div>
        </div>

        {/* ─── Verified Badge ─── */}
        {isManager && (() => {
          const elig = isEligibleForBadge(managerProfile);
          const hasBadge = !!managerProfile?.verified_badge;
          return (
            <>
              <SectionHeader title="Verified Badge" />
              <div style={{ ...cardStyle, padding: 18, marginBottom: 24, borderRadius: '0 0 16px 16px', borderTop: 'none' }}>
                <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 26, color: '#C9972A', lineHeight: 1 }}>★</div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#0F2C4E' }}>
                      ${BADGE_PRICE_USD}/year
                    </div>
                    <div style={{ fontSize: 12, color: '#6B6358', marginTop: 2 }}>
                      {hasBadge
                        ? 'Active. Showing on your public profile.'
                        : elig.eligible
                          ? 'You qualify. Purchase to display the gold ★ on your profile.'
                          : `${elig.failing.join(', ')}.`}
                    </div>
                  </div>
                  {/* Stacked pills on the right */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 8, minWidth: 160 }}>
                    {hasBadge ? (
                      <span style={pillBtnGoldFill}>★ ACTIVE</span>
                    ) : (
                      <>
                        <span style={pillBtnCream}>
                          {elig.eligible ? 'Eligible' : 'Not yet eligible'}
                        </span>
                        <button onClick={() => setShowBadgeInfo(true)} style={pillBtnCream}>
                          Inquire
                        </button>
                        <a
                          href={'mailto:support@renterrep.com?subject=Verified%20Badge%20request&body=' + encodeURIComponent(`Hi RenterRep team,\n\nI'd like to request the Verified Badge.\n\nName: ${user?.name || ''}\nEmail: ${user?.email || ''}\n`)}
                          style={{ ...pillBtnCream, textDecoration: 'none' }}
                        >
                          Request a badge
                        </a>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </>
          );
        })()}

        {/* ─── Tenant screening (credit reports) ─── */}
        {isManager && (
          <>
            <SectionHeader title="Tenant Screening" subtitle="Credit reports you've requested" />
            <div style={{ ...cardStyle, padding: 18, marginBottom: 24, borderRadius: '0 0 16px 16px', borderTop: 'none' }}>
              {/* Always-visible 'how to run one' panel */}
              <div style={{
                marginBottom: creditRequests.length === 0 ? 14 : 16,
                padding: '14px 16px',
                background: 'rgba(201,151,42,0.10)',
                border: '1px dashed rgba(201,151,42,0.55)',
                borderRadius: 10,
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0F2C4E', marginBottom: 4 }}>
                  📄 How to run a credit report
                </div>
                <div style={{ fontSize: 11, color: '#6B6358', marginBottom: 10 }}>
                  Soft inquiry · tenant must authorize · <b>free during launch</b> ($35/report after launch)
                </div>
                <ol style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: '#2C2C2C', lineHeight: 1.7 }}>
                  <li>Find a tenant — click <b>Find a tenant</b> below to search.</li>
                  <li>Open their profile.</li>
                  <li>Click the gold <b>Request credit report</b> button.</li>
                  <li>Copy the tenant consent link and send it to them.</li>
                  <li>Once they authorize, the report appears here as <b>Ready to view</b>.</li>
                </ol>
                <button
                  onClick={() => navigate('/search?type=tenant')}
                  style={{ marginTop: 12, background: '#C9972A', color: '#fff', border: 'none', padding: '8px 18px', borderRadius: 50, fontSize: 12, fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 8px rgba(201,151,42,0.30)' }}
                >
                  Find a tenant →
                </button>
              </div>
              {creditRequests.length === 0
                ? <Empty>
                    No credit reports requested yet. Follow the steps above to run your first one.
                  </Empty>
                : <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {creditRequests.map(r => (
                      <div key={r.id} style={{ ...miniCard, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#0F2C4E' }}>{r.tenant_name || 'Tenant'}</div>
                          <div style={{ fontSize: 11, color: '#6B6358', marginTop: 2 }}>
                            Requested {new Date(r.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        <CreditStatusPill status={r.status} />
                        <button
                          onClick={() => navigate('/credit/report/' + r.id)}
                          style={{ background: r.status === 'ready' ? '#0F2C4E' : 'transparent', color: r.status === 'ready' ? '#fff' : '#0F2C4E', border: r.status === 'ready' ? 'none' : '1.5px solid #0F2C4E', padding: '7px 14px', borderRadius: 50, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                        >
                          {r.status === 'ready' ? 'View report' : 'Details'}
                        </button>
                      </div>
                    ))}
                  </div>
              }
            </div>
          </>
        )}

        {/* ─── Reputation ─── */}
        <SectionHeader title="Reputation" subtitle="How you're scored, who reviewed you, what you've reviewed" />
        <div style={{ ...cardStyle, padding: 18, marginBottom: 24, borderRadius: '0 0 16px 16px', borderTop: 'none' }}>
          {/* Your score */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 11, color: '#B8893A', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>Your overall score</div>
              <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 28, fontWeight: 400, color: reputation >= 4 ? '#1B7A4B' : reputation >= 3 ? '#C9972A' : reputation ? '#C0392B' : '#6B6358', lineHeight: 1 }}>
                {reputation ? reputation.toFixed(1) : '—'}
              </div>
              <div style={{ fontSize: 12, color: '#6B6358', marginTop: 4 }}>
                {reviewsAboutMe.length === 0 ? 'No reviews yet' : `${reviewsAboutMe.length} review${reviewsAboutMe.length === 1 ? '' : 's'} from tenants`}
              </div>
            </div>
            {reputation && <StarRating rating={reputation} size={20} />}
          </div>

          {/* Reviews about me */}
          <SubHeader>Reviews about you</SubHeader>
          {loading ? <Loading /> :
            reviewsAboutMe.length === 0
              ? <Empty>No tenant has reviewed you yet. Reviews appear here as tenants you've rented to submit them.</Empty>
              : <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}>
                  {reviewsAboutMe.map(r => (
                    <div key={r.id} style={miniCard}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#0F2C4E' }}>{r.author}</div>
                          <div style={{ fontSize: 11, color: '#6B6358', marginTop: 2 }}>{r.date}</div>
                        </div>
                        <StarRating rating={r.rating} size={12} />
                      </div>
                      {r.text && <p style={{ fontSize: 12.5, color: '#2C2C2C', lineHeight: 1.6, margin: 0 }}>{r.text}</p>}
                    </div>
                  ))}
                </div>
          }

          {/* Profiles you've viewed */}
          <SubHeader>Profiles you've viewed</SubHeader>
          {viewedProfiles.length === 0
            ? <Empty>You haven't looked up any profiles yet.</Empty>
            : <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
                {viewedProfiles.map(p => (
                  <button key={p.id} onClick={() => navigate('/profile/' + p.id)} style={{
                    ...miniCard, cursor: 'pointer', textAlign: 'left',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <span style={{ fontSize: 13, fontWeight: 500, color: '#0F2C4E' }}>{p.name}</span>
                    <span style={{ fontSize: 11, color: '#6B6358' }}>View again →</span>
                  </button>
                ))}
              </div>
          }

          {/* My reviews */}
          <SubHeader>Reviews you've written</SubHeader>
          {loading ? <Loading /> :
            myReviews.length === 0
              ? <Empty>You haven't written any reviews yet. <a href="#" onClick={(e) => { e.preventDefault(); navigate('/find?type=tenant'); }} style={{ color: '#0F2C4E', fontWeight: 600 }}>Write your first review →</a></Empty>
              : <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {myReviews.map(r => (
                    <div key={r.id} style={miniCard}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#0F2C4E' }}>{r.profileName}</div>
                          <div style={{ fontSize: 11, color: '#6B6358', marginTop: 2 }}>{r.date} · {r.profile_type === 'manager' ? 'Property Management' : 'Tenant'}</div>
                        </div>
                        <StarRating rating={r.rating} size={12} />
                      </div>
                      {r.text && <p style={{ fontSize: 12.5, color: '#2C2C2C', lineHeight: 1.6, marginBottom: 10 }}>{r.text}</p>}
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => navigate(`/review/${r.profile_id}?edit=${r.id}`)} style={smallBtnNavy}>Edit</button>
                        <button onClick={() => handleDeleteMyReview(r.id)} style={smallBtnRed}>Delete</button>
                        <button onClick={() => navigate(`/profile/${r.profile_id}`)} style={smallBtnSubtle}>View profile</button>
                      </div>
                    </div>
                  ))}
                </div>
          }
        </div>

      </div>

      {showBadgeInfo && (
        <div
          onClick={() => setShowBadgeInfo(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,44,78,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#FAF6EC', borderRadius: 16, padding: 28, maxWidth: 520, width: '100%', border: '1px solid rgba(184,137,58,0.22)', boxShadow: '0 12px 32px rgba(15,44,78,0.25)' }}
          >
            <div style={{ fontSize: 32, marginBottom: 8, color: '#C9972A', textAlign: 'center' }}>★</div>
            <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 22, fontWeight: 400, color: '#0F2C4E', textAlign: 'center', marginBottom: 14 }}>
              About the Verified Badge
            </h2>
            <p style={{ fontSize: 13, color: '#2C2C2C', lineHeight: 1.65, marginBottom: 12 }}>
              The Verified Badge is a gold <b>★ VERIFIED</b> pill we display next to your name on your public RenterRep profile, and which you can embed on your own website.
              It signals to prospective tenants that you're a property manager in good standing.
            </p>
            <div style={{ fontSize: 11, color: '#B8893A', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>Price</div>
            <p style={{ fontSize: 13, color: '#2C2C2C', marginBottom: 12 }}>${BADGE_PRICE_USD} per year.</p>
            <div style={{ fontSize: 11, color: '#B8893A', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>Eligibility</div>
            <ul style={{ paddingLeft: 18, margin: 0, marginBottom: 14, fontSize: 13, color: '#2C2C2C', lineHeight: 1.7 }}>
              <li>⭐ 4.0+ average rating</li>
              <li>3+ verified reviews</li>
              <li>Account active 60+ days</li>
              <li>No active suspension or unresolved dispute</li>
            </ul>
            <div style={{ fontSize: 11, color: '#B8893A', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>How renewal works</div>
            <p style={{ fontSize: 13, color: '#2C2C2C', lineHeight: 1.65, marginBottom: 18 }}>
              Eligibility is rechecked at renewal. If your rating drops below 4.0 mid-year you get a 30-day grace window before the badge is removed — one bad review won't strip you instantly.
            </p>
            <div style={{ textAlign: 'center' }}>
              <button onClick={() => setShowBadgeInfo(false)} style={pillBtnCream}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SectionHeader({ title, subtitle }) {
  return (
    <div>
      <div style={{
        background: '#E5D5B7',
        color: '#0F2C4E',
        fontFamily: "'DM Serif Display', serif",
        fontSize: 13,
        fontWeight: 400,
        textAlign: 'center',
        letterSpacing: '0.10em',
        padding: '5px 22px',
        borderRadius: '16px 16px 0 0',
        textShadow: '0 1px 1px rgba(255,255,255,0.35)',
        boxShadow: '0 2px 6px rgba(120,90,30,0.14)',
      }}>{title.toUpperCase()}</div>
      {subtitle && (
        <div style={{
          fontSize: 12, color: '#6B6358',
          background: '#FAF6EC',
          padding: '8px 18px 0',
          borderLeft: '1px solid rgba(184,137,58,0.22)',
          borderRight: '1px solid rgba(184,137,58,0.22)',
        }}>{subtitle}</div>
      )}
    </div>
  );
}
function SubHeader({ children }) {
  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid rgba(184,137,58,0.22)' }}>
      <div style={{ fontSize: 11, color: '#B8893A', letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 8 }}>{children}</div>
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(184,137,58,0.12)', fontSize: 13 }}>
      <span style={{ color: '#6B6358' }}>{label}</span>
      <span style={{ color: '#0F2C4E', fontWeight: 500 }}>{value || '—'}</span>
    </div>
  );
}
function Loading() { return <div style={{ fontSize: 12, color: '#6B6358', fontStyle: 'italic', padding: '8px 0', marginBottom: 14 }}>Loading…</div>; }
function CreditStatusPill({ status }) {
  const map = {
    pending_consent: { bg: '#FFF8E1', fg: '#7B5E12', border: '#C9972A' },
    consented:       { bg: '#E6F0FB', fg: '#0F2C4E', border: '#0F2C4E' },
    ready:           { bg: '#E8F5EE', fg: '#1B7A4B', border: '#1B7A4B' },
    declined:        { bg: '#FDEDEB', fg: '#C0392B', border: '#C0392B' },
    expired:         { bg: '#F5EFE3', fg: '#6B6358', border: '#6B6358' },
  };
  const c = map[status] || map.pending_consent;
  return (
    <span style={{ background: c.bg, color: c.fg, border: '1px solid ' + c.border, padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: '0.04em' }}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}
function Empty({ children }) { return <div style={{ fontSize: 12.5, color: '#6B6358', fontStyle: 'italic', padding: '8px 0', marginBottom: 18 }}>{children}</div>; }

const miniCard = { background: '#F5EFE3', border: '1px solid rgba(184,137,58,0.18)', borderRadius: 12, padding: 12 };
// Shared cream-gold action pill — used for every clickable pill on
// the dashboard so they all look the same (Delete plan, Manage
// payment, Inquire, Request a badge, Not yet eligible).
const pillBtnCream = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  background: 'rgba(201,151,42,0.15)',
  color: '#8A6F2E',
  border: '1px solid rgba(201,151,42,0.55)',
  padding: '6px 14px', borderRadius: 50,
  fontSize: 12, fontWeight: 700, letterSpacing: '0.04em',
  cursor: 'pointer', textAlign: 'center', lineHeight: 1.4,
};
const pillBtnGoldFill = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  background: '#C9972A', color: '#fff',
  padding: '6px 14px', borderRadius: 50,
  fontSize: 11, fontWeight: 800, letterSpacing: '0.06em',
  boxShadow: '0 2px 8px rgba(201,151,42,0.30)',
  textAlign: 'center', lineHeight: 1.4,
};
const btnOutline = { background: 'transparent', border: '1.5px solid #0F2C4E', color: '#0F2C4E', padding: '7px 14px', borderRadius: 50, fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const smallBtnNavy = { flex: 1, padding: '7px 10px', borderRadius: 50, border: '1.5px solid #0F2C4E', background: 'transparent', color: '#0F2C4E', fontSize: 12, fontWeight: 700, cursor: 'pointer' };
const smallBtnRed = { flex: 1, padding: '7px 10px', borderRadius: 50, border: '1.5px solid #C0392B', background: 'transparent', color: '#C0392B', fontSize: 12, fontWeight: 700, cursor: 'pointer' };
const smallBtnSubtle = { flex: 1, padding: '7px 10px', borderRadius: 50, border: '1.5px solid rgba(184,137,58,0.45)', background: '#FAF6EC', color: '#6B6358', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const eyeBtn = { position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#6B6358', fontSize: 14 };
